import { ElasticApm, ApmOptions } from './model';
import NioApmPlusClient from './client';
import { createBrowserClient } from '@apmplus/web';
import { currentInLgContainer, currentInNioCNAndroid, initRuntime } from './util';

export { default as NioApmPlusClient } from './client';
export * from './model';

export class NioApmPlus {
    private static DEFAULT_INSTANCE_CLIENT_NAME = 'NIO_APM_PLUS';
    private static clientMap: Map<string, NioApmPlusClient> = new Map();
    private static hasInitLgJSB = false;

    private static async initLgJSB(): Promise<undefined> {
        return new Promise<undefined>((resolve) => {
            try {
                if (currentInLgContainer() && currentInNioCNAndroid()) {
                    // @ts-ignore
                    window._dsbridge.init();
                    setTimeout(() => {
                        resolve(void 0);
                    }, 100);
                } else {
                    resolve(void 0);
                }
            } catch (err) {
                console.error('[NIO APM Plus] init lg jsb failed, error: ', err);
                resolve(void 0);
            }
        });
    }

    /**
     * 异步创建默认 APM plus 实例
     * @param options
     */
    public static async createDefaultInstance(options: ApmOptions) {
        return this.createInstance(this.DEFAULT_INSTANCE_CLIENT_NAME, options);
    }

    /**
     * 异步创建 APM plus 实例
     * @param clientName
     * @param options
     */
    public static async createInstance(clientName: string, options: ApmOptions) {
        try {
            initRuntime();
            if (options.needInitJSB && !this.hasInitLgJSB) {
                await this.initLgJSB();
                this.hasInitLgJSB = true;
            }
            const client = new NioApmPlusClient(clientName, options);
            client.initialize();
            this.clientMap.set(clientName, client);
            return client;
        } catch (err) {
            console.error(`[NIO APM Plus] create client ${clientName} failed, error: `, err);
            return void 0;
        }
    }

    /**
     * 同步创建默认 APM plus 实例
     * @param options
     */
    public static createDefaultInstanceSync(options: Omit<ApmOptions, 'needInitJSB'>) {
        return this.createInstanceSync(this.DEFAULT_INSTANCE_CLIENT_NAME, options);
    }

    /**
     * 同步创建 APM plus 实例
     * @param clientName
     * @param options
     */
    public static createInstanceSync(clientName: string, options: Omit<ApmOptions, 'needInitJSB'>) {
        try {
            initRuntime();
            const client = new NioApmPlusClient(clientName, options as ApmOptions);
            client.initialize();
            this.clientMap.set(clientName, client);
            return client;
        } catch (err) {
            console.error(`[NIO APM Plus] create client ${clientName} failed, error: `, err);
            return void 0;
        }
    }

    /**
     * 获取默认 APM plus 实例
     */
    public static getDefaultInstance() {
        return this.getInstance(this.DEFAULT_INSTANCE_CLIENT_NAME);
    }

    /**
     * 获取 APM plus 实例
     * @param clientName
     */
    public static getInstance(clientName: string) {
        return this.clientMap.get(clientName);
    }

    /**
     * 获取默认 APM plus client 实例
     */
    public static getDefaultClient(): ReturnType<typeof createBrowserClient> | undefined | null {
        return this.getClient(this.DEFAULT_INSTANCE_CLIENT_NAME);
    }

    /**
     * 获取 APM plus client 实例
     * @param clientName
     */
    public static getClient(clientName: string): ReturnType<typeof createBrowserClient> | undefined | null {
        return this.clientMap.get(clientName)?.client;
    }

    /**
     * 销毁实例
     * 调用此方法可以去掉所有监听和副作用，包括hook或者内存对象。当前实例本身还是可用的，所以手动调用API依然可以上报数据。
     * @param clientName
     */
    public static destroyInstance(clientName: string) {
        const instance = this.getInstance(clientName);

        instance?.destroy();

        // this.clientMap.delete(clientName);
    }

    /**
     * 获取Elastic APM实例
     * @returns 
     */
    public static getDefaultApmInstance(): ElasticApm | null {
        return this.getDefaultInstance()?.getApmInstance() ?? null;
    }
}
